function getIdleOverlayAssetUrl(filename) {
  const configuredAssets = window.IdleOverlayConfig && window.IdleOverlayConfig.assets;
  if (configuredAssets) {
    if (filename === 'idle-overlay.jpg' && typeof configuredAssets.desktop === 'string' && configuredAssets.desktop) {
      return configuredAssets.desktop;
    }
    if (
      filename === 'idle-overlay-mobile.jpg' &&
      typeof configuredAssets.mobile === 'string' &&
      configuredAssets.mobile
    ) {
      return configuredAssets.mobile;
    }
  }

  const scriptEl = Array.from(document.scripts).find(
    (script) => script.src && script.src.includes('/assets/idle-overlay.js'),
  );

  if (!scriptEl || !scriptEl.src) return filename;

  try {
    const scriptUrl = new URL(scriptEl.src, window.location.href);
    const basePath = scriptUrl.pathname.replace(/idle-overlay\.js$/, '');
    return `${scriptUrl.origin}${basePath}${filename}`;
  } catch {
    return filename;
  }
}

function getConfiguredTimeoutSeconds() {
  const rawValue = window.IdleOverlayConfig && window.IdleOverlayConfig.timeoutSeconds;
  const parsedValue = Number.parseInt(rawValue, 10);
  if (Number.isFinite(parsedValue) && parsedValue > 0) return parsedValue;
  return null;
}

function isIdleOverlayDebugEnabled() {
  return window.IdleOverlayConfig && window.IdleOverlayConfig.debug === true;
}

function idleOverlayLog(level, message, details) {
  if (!isIdleOverlayDebugEnabled()) return;
  const logger = console[level] || console.log;
  if (details !== undefined) {
    logger(`[idle-overlay] ${message}`, details);
  } else {
    logger(`[idle-overlay] ${message}`);
  }
}

function waitForImage(img) {
  if (!(img instanceof HTMLImageElement)) return Promise.resolve();
  if (img.complete && img.naturalWidth > 0) {
    return typeof img.decode === 'function' ? img.decode().catch(() => {}) : Promise.resolve();
  }
  return new Promise((resolve) => {
    const done = () => {
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
      if (typeof img.decode === 'function') {
        img.decode().catch(() => {}).finally(resolve);
      } else {
        resolve();
      }
    };
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
  });
}

function primeIdleOverlayImageSources(img) {
  if (!(img instanceof HTMLImageElement)) return;

  const picture = img.closest('picture');
  const source = picture?.querySelector('source[data-srcset]');

  if (source instanceof HTMLSourceElement && !source.srcset && source.dataset.srcset) {
    source.srcset = source.dataset.srcset;
  }

  if (!img.getAttribute('src') && img.dataset.src) {
    img.src = img.dataset.src;
  }
}

function applyIdleOverlayBaseStyles({ overlay, imgWrapper, picture, img, vignette, shimmer }) {
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '2147483647',
    width: '100vw',
    height: '100vh',
    overflow: 'hidden',
    opacity: '0',
    pointerEvents: 'none',
    display: 'block',
  });

  Object.assign(imgWrapper.style, {
    position: 'absolute',
    inset: '-5%',
    width: '110%',
    height: '110%',
    willChange: 'transform, filter',
    transform: 'scale(1.08)',
    filter: 'blur(18px) brightness(0.7) saturate(1.2)',
  });

  Object.assign(picture.style, {
    position: 'absolute',
    inset: '0',
    display: 'block',
    width: '100%',
    height: '100%',
  });

  Object.assign(img.style, {
    position: 'absolute',
    inset: '0',
    display: 'block',
    width: '100%',
    height: '100%',
    maxWidth: 'none',
    objectFit: 'cover',
    objectPosition: 'center center',
  });

  Object.assign(vignette.style, {
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%)',
    opacity: '0',
    willChange: 'opacity',
  });

  Object.assign(shimmer.style, {
    position: 'absolute',
    top: '-100%',
    left: '0',
    width: '100%',
    height: '60%',
    pointerEvents: 'none',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, transparent 100%)',
    opacity: '0',
    willChange: 'transform, opacity',
  });
}

function hydrateIdleOverlayElement(overlay) {
  if (!(overlay instanceof HTMLElement)) return null;

  const desktopSrc = getIdleOverlayAssetUrl('idle-overlay.jpg');
  const mobileSrc = getIdleOverlayAssetUrl('idle-overlay-mobile.jpg');

  overlay.classList.add('idle-overlay');

  const imgWrapper = document.createElement('div');
  imgWrapper.className = 'idle-overlay__media';
  imgWrapper.setAttribute('data-idle-img-wrapper', '');

  const picture = document.createElement('picture');
  picture.className = 'idle-overlay__picture';

  const source = document.createElement('source');
  source.media = '(max-width: 749px)';
  source.dataset.srcset = mobileSrc;

  const img = document.createElement('img');
  img.className = 'idle-overlay__image';
  img.dataset.src = desktopSrc;
  img.alt = 'Schermata di inattivita. Clicca o scorri per tornare al sito.';
  img.width = 1600;
  img.height = 1600;
  img.loading = 'lazy';
  img.decoding = 'async';

  const vignette = document.createElement('div');
  vignette.className = 'idle-overlay__vignette';
  vignette.setAttribute('data-idle-vignette', '');

  const shimmer = document.createElement('div');
  shimmer.className = 'idle-overlay__shimmer';
  shimmer.setAttribute('data-idle-shimmer', '');

  picture.appendChild(source);
  picture.appendChild(img);
  imgWrapper.appendChild(picture);
  overlay.replaceChildren(imgWrapper, vignette, shimmer);

  applyIdleOverlayBaseStyles({ overlay, imgWrapper, picture, img, vignette, shimmer });

  idleOverlayLog('info', 'Overlay media hydrated', { desktopSrc, mobileSrc });

  return { img, imgWrapper, vignette, shimmer };
}

function createOverlayElement() {
  if (!(document.body instanceof HTMLBodyElement)) return null;

  const configuredTimeoutSeconds = getConfiguredTimeoutSeconds();
  const timeoutSeconds = configuredTimeoutSeconds || 90;
  const desktopSrc = getIdleOverlayAssetUrl('idle-overlay.jpg');

  // Wrapper principale
  const overlay = document.createElement('div');
  overlay.className = 'idle-overlay';
  overlay.setAttribute('data-idle-overlay', '');
  overlay.setAttribute('data-idle-timeout-seconds', String(timeoutSeconds));
  overlay.setAttribute('aria-hidden', 'true');

  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '2147483647',
    width: '100vw',
    height: '100vh',
    overflow: 'hidden',
    opacity: '0',
    pointerEvents: 'none',
    display: 'block',
  });

  // Layer immagine — scala e blur animati separatamente
  const imgWrapper = document.createElement('div');
  imgWrapper.setAttribute('data-idle-img-wrapper', '');
  Object.assign(imgWrapper.style, {
    position: 'absolute',
    inset: '-5%',        // leggermente più grande per lo zoom senza bordi
    width: '110%',
    height: '110%',
    willChange: 'transform, filter',
    transform: 'scale(1.08)',
    filter: 'blur(18px) brightness(0.7) saturate(1.2)',
  });

  const img = document.createElement('img');
  img.dataset.src = desktopSrc;
  img.alt = 'Schermata di inattivita. Clicca o scorri per tornare al sito.';
  img.width = 1600;
  img.height = 1600;
  img.loading = 'lazy';
  img.decoding = 'async';

  Object.assign(img.style, {
    position: 'absolute',
    inset: '0',
    display: 'block',
    width: '100%',
    height: '100%',
    maxWidth: 'none',
    objectFit: 'cover',
    objectPosition: 'center center',
  });

  // Vignette overlay — aggiunge profondità cinematografica
  const vignette = document.createElement('div');
  vignette.setAttribute('data-idle-vignette', '');
  Object.assign(vignette.style, {
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%)',
    opacity: '0',
    willChange: 'opacity',
  });

  // Shimmer — linea di luce sottile che scorre dall'alto
  const shimmer = document.createElement('div');
  shimmer.setAttribute('data-idle-shimmer', '');
  Object.assign(shimmer.style, {
    position: 'absolute',
    top: '-100%',
    left: '0',
    width: '100%',
    height: '60%',
    pointerEvents: 'none',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, transparent 100%)',
    opacity: '0',
    willChange: 'transform, opacity',
  });

  imgWrapper.appendChild(img);
  overlay.appendChild(imgWrapper);
  overlay.appendChild(vignette);
  overlay.appendChild(shimmer);
  document.body.appendChild(overlay);

  idleOverlayLog('info', 'Overlay element created', { desktopSrc });
  return overlay;
}

function initIdleOverlay() {
  window.IdleOverlayDebug = window.IdleOverlayDebug || {};
  window.IdleOverlayDebug.moduleLoaded = true;
  window.IdleOverlayDebug.initialized = false;

  let overlay = document.querySelector('[data-idle-overlay]');

  if (!(overlay instanceof HTMLElement)) {
    if (!(document.body instanceof HTMLBodyElement)) {
      idleOverlayLog('warn', 'Document body is not ready for overlay creation');
      window.IdleOverlayDebug.reason = 'body-not-ready';
      return false;
    }

    overlay = document.createElement('div');
    overlay.className = 'idle-overlay';
    overlay.setAttribute('data-idle-overlay', '');
    overlay.setAttribute(
      'data-idle-timeout-seconds',
      String(getConfiguredTimeoutSeconds() || 90),
    );
    overlay.setAttribute('aria-hidden', 'true');
    document.body.appendChild(overlay);
    idleOverlayLog('info', 'Overlay element created');
  }

  if (!(overlay instanceof HTMLElement)) {
    idleOverlayLog('warn', 'Overlay element not found and fallback creation failed');
    window.IdleOverlayDebug.reason = 'overlay-not-found';
    return false;
  }

  if (overlay.dataset.idleOverlayBound === 'true') {
    idleOverlayLog('info', 'Overlay already initialized');
    return true;
  }

  const hydratedOverlay = hydrateIdleOverlayElement(overlay);

  if (!hydratedOverlay) {
    idleOverlayLog('warn', 'Overlay hydration failed');
    window.IdleOverlayDebug.reason = 'overlay-hydration-failed';
    return false;
  }

  overlay.dataset.idleOverlayBound = 'true';
  overlay.removeAttribute('hidden');
  overlay.style.opacity = '0';
  overlay.style.pointerEvents = 'none';

  window.IdleOverlayDebug.initialized = true;
  window.IdleOverlayDebug.reason = 'ok';

  const configuredTimeoutSeconds = getConfiguredTimeoutSeconds();
  const timeoutSeconds =
    configuredTimeoutSeconds || Number.parseInt(overlay.dataset.idleTimeoutSeconds || '', 10);
  const idleTimeoutMs = Number.isFinite(timeoutSeconds) && timeoutSeconds > 0 ? timeoutSeconds * 1000 : 90000;

  const activityEvents = ['pointerdown', 'touchstart', 'keydown', 'wheel'];
  const dismissEvents  = ['pointerdown', 'touchstart', 'wheel'];

  let idleTimerId     = 0;
  let isVisible       = false;
  let lastScheduleAt  = 0;
  let activeAnims     = [];

  const { img, imgWrapper, vignette, shimmer } = hydratedOverlay;

  idleOverlayLog('info', 'Initialized', { timeoutSeconds: idleTimeoutMs / 1000 });

  function cancelAll() {
    activeAnims.forEach((a) => { try { a.cancel(); } catch (_) {} });
    activeAnims = [];
  }

  function closeMediaQuickCheckoutDialogs() {
    const closePromises = [];
    const handledHosts = new Set();
    const openDialogs = document.querySelectorAll(
      "dialog.quick-add-modal[data-media-quick-checkout='true'][open], dialog.product-media-quick-checkout-fallback[data-media-quick-checkout='true'][open]",
    );

    openDialogs.forEach((dialog) => {
      if (!(dialog instanceof HTMLDialogElement)) return;

      const parentQuickAdd = dialog.closest('quick-add-dialog');
      if (
        parentQuickAdd instanceof HTMLElement &&
        typeof parentQuickAdd.closeDialog === 'function' &&
        !handledHosts.has(parentQuickAdd)
      ) {
        handledHosts.add(parentQuickAdd);
        try {
          const closeResult = parentQuickAdd.closeDialog();
          if (closeResult && typeof closeResult.then === 'function') {
            closePromises.push(closeResult.catch(() => {}));
          }
        } catch (_error) {
          try {
            dialog.close();
          } catch (_fallbackError) {
            dialog.removeAttribute('open');
          }
        }
        return;
      }

      try {
        dialog.close();
      } catch (_error) {
        dialog.removeAttribute('open');
      }
    });

    return Promise.allSettled(closePromises);
  }

  function showOverlay() {
    if (isVisible) return;

    idleOverlayLog('info', 'Showing overlay');

    const closeDialogsTask = Promise.race([
      closeMediaQuickCheckoutDialogs(),
      new Promise((resolve) => window.setTimeout(resolve, 420)),
    ]);

    closeDialogsTask.finally(() => {
      primeIdleOverlayImageSources(img);
      waitForImage(img).then(() => {
      isVisible = true;
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.body.style.overscrollBehavior = 'none';

      cancelAll();

      // Reset iniziale imgWrapper
      imgWrapper.style.transform = 'scale(1.08)';
      imgWrapper.style.filter    = 'blur(18px) brightness(0.7) saturate(1.2)';
      vignette.style.opacity     = '0';
      shimmer.style.opacity      = '0';
      shimmer.style.transform    = 'translateY(-100%)';

      // 1. Fade-in dell'intero overlay
      const fadeInOverlay = overlay.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: 900, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' }
      );
      activeAnims.push(fadeInOverlay);
      fadeInOverlay.addEventListener('finish', () => { overlay.style.opacity = '1'; overlay.style.pointerEvents = 'auto'; });

      // 2. Zoom lento + blur che svanisce sull'immagine (effetto "messa a fuoco")
      const zoomBlur = imgWrapper.animate(
        [
          { transform: 'scale(1.08)', filter: 'blur(18px) brightness(0.7) saturate(1.2)' },
          { transform: 'scale(1.03)', filter: 'blur(6px)  brightness(0.8) saturate(1.1)', offset: 0.4 },
          { transform: 'scale(1.0)',  filter: 'blur(0px)  brightness(1.0) saturate(1.0)' },
        ],
        { duration: 2800, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' }
      );
      activeAnims.push(zoomBlur);
      zoomBlur.addEventListener('finish', () => {
        imgWrapper.style.transform = 'scale(1.0)';
        imgWrapper.style.filter    = 'blur(0px) brightness(1.0) saturate(1.0)';
      });

      // 3. Vignette appare lentamente
      const vignetteIn = vignette.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: 2200, delay: 400, easing: 'ease-out', fill: 'forwards' }
      );
      activeAnims.push(vignetteIn);
      vignetteIn.addEventListener('finish', () => { vignette.style.opacity = '1'; });

      // 4. Shimmer — linea di luce che scorre dall'alto verso il basso una sola volta
      const shimmerAnim = shimmer.animate(
        [
          { opacity: 0,   transform: 'translateY(-100%)' },
          { opacity: 1,   transform: 'translateY(0%)',   offset: 0.1 },
          { opacity: 0.6, transform: 'translateY(80%)',  offset: 0.7 },
          { opacity: 0,   transform: 'translateY(160%)' },
        ],
        { duration: 2000, delay: 600, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' }
      );
      activeAnims.push(shimmerAnim);
      shimmerAnim.addEventListener('finish', () => { shimmer.style.opacity = '0'; });
      });
    });
  }

  function clearIdleTimer() {
    if (!idleTimerId) return;
    window.clearTimeout(idleTimerId);
    idleTimerId = 0;
  }

  function scheduleOverlay() {
    const now = Date.now();
    if (now - lastScheduleAt < 200) return;
    lastScheduleAt = now;

    clearIdleTimer();

    if (document.hidden) {
      idleOverlayLog('info', 'Skip schedule because document is hidden');
      return;
    }

    idleOverlayLog('info', 'Scheduling overlay', { timeoutMs: idleTimeoutMs });

    idleTimerId = window.setTimeout(() => {
      showOverlay();
    }, idleTimeoutMs);
  }

  function hideOverlay(resetTimer = true) {
    if (!isVisible) {
      if (resetTimer) scheduleOverlay();
      return;
    }

    idleOverlayLog('info', 'Hiding overlay', { resetTimer });

    isVisible = false;
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.body.style.overscrollBehavior = '';

    cancelAll();

    const currentOpacity = parseFloat(overlay.style.opacity) || 1;

    // Fade-out rapido con leggero zoom-out
    const fadeOut = overlay.animate(
      [{ opacity: currentOpacity }, { opacity: 0 }],
      { duration: 700, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' }
    );
    activeAnims.push(fadeOut);

    const zoomOut = imgWrapper.animate(
      [
        { transform: imgWrapper.style.transform || 'scale(1.0)' },
        { transform: 'scale(1.04)' },
      ],
      { duration: 700, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' }
    );
    activeAnims.push(zoomOut);

    fadeOut.addEventListener('finish', () => {
      overlay.style.opacity      = '0';
      overlay.style.pointerEvents = 'none';
      // Reset per la prossima volta
      imgWrapper.style.transform  = 'scale(1.08)';
      imgWrapper.style.filter     = 'blur(18px) brightness(0.7) saturate(1.2)';
      vignette.style.opacity      = '0';
      if (resetTimer) scheduleOverlay();
    });
  }

  function handleIdleActivity() {
    if (isVisible) return;
    scheduleOverlay();
  }

  function handleDismiss(event) {
    if (!isVisible) return;
    if (event.type === 'keydown' && event.key === 'Tab') return;

    idleOverlayLog('info', 'Dismiss requested', { eventType: event.type });

    if (event.cancelable) event.preventDefault();
    event.stopPropagation();
    hideOverlay(true);
  }

  activityEvents.forEach((eventName) => {
    document.addEventListener(eventName, handleIdleActivity, { passive: true });
  });

  window.addEventListener('scroll', handleIdleActivity, { passive: true });

  dismissEvents.forEach((eventName) => {
    overlay.addEventListener(eventName, handleDismiss, { passive: false });
  });

  document.addEventListener('keydown', handleDismiss, true);

  document.addEventListener('visibilitychange', () => {
    idleOverlayLog('info', 'Visibility changed', { hidden: document.hidden });
    if (document.hidden) {
      clearIdleTimer();
      hideOverlay(false);
      return;
    }
    scheduleOverlay();
  });

  window.addEventListener('pageshow', scheduleOverlay);
  window.addEventListener('pagehide', () => { clearIdleTimer(); });

  window.IdleOverlayDebug = {
    moduleLoaded: true,
    initialized: true,
    reason: 'ok',
    overlay,
    show() {
      clearIdleTimer();
      showOverlay();
      idleOverlayLog('info', 'Forced show from debug API');
    },
    hide() { hideOverlay(false); },
    schedule() { scheduleOverlay(); },
  };

  idleOverlayLog('info', 'Debug API ready on window.IdleOverlayDebug');

  scheduleOverlay();
  return true;
}

function bootstrapIdleOverlay() {
  const initialized = initIdleOverlay();
  if (initialized) return;

  const retry = () => {
    if (initIdleOverlay()) {
      window.removeEventListener('pageshow', retry);
    }
  };

  window.addEventListener('pageshow', retry);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrapIdleOverlay, { once: true });
} else {
  bootstrapIdleOverlay();
}
